/**
 * Upstream cost of AI usage rows from `fetchGroupAIUsage` /
 * `fetchAllGroupsAIUsage`. PRATUS passes the provider's charge through without
 * markup, so these totals are what Azure OpenAI and the external data APIs
 * billed. COMS sets `cost_usd` per row (recorded at call time, or estimated
 * from the model's list price for older rows — `cost_estimated`); a null
 * `cost_usd` means the row predates cost tracking and has no known model, so it
 * is counted as unpriced rather than guessed.
 */

export interface AIUsageRecord {
  timestamp: string;
  prompt_tokens: number;
  completion_tokens: number;
  source?: 'llm' | 'tool_api' | null;
  cost_usd?: number | null;
  cost_estimated?: boolean;
}

export interface UpstreamCost {
  /** Azure OpenAI model calls. */
  modelCost: number;
  /** External data APIs called by agent tools (web search, geocoding, …). */
  dataServiceCost: number;
  total: number;
  /**
   * Some of the total comes from list-price estimates of rows recorded before
   * costs were tracked.
   */
  includesEstimates: boolean;
  /** Model tokens with no known cost. */
  unpricedTokens: number;
}

/**
 * Records whose timestamp falls in the given calendar month (1-12), in the
 * viewer's local time.
 */
export function recordsForMonth<T extends AIUsageRecord>(
  records: readonly T[],
  year: number,
  month: number
): T[] {
  return records.filter((r) => {
    const date = new Date(r.timestamp);
    return date.getFullYear() === year && date.getMonth() + 1 === month;
  });
}

export function summarizeUpstreamCost(
  records: readonly AIUsageRecord[]
): UpstreamCost {
  const summary: UpstreamCost = {
    modelCost: 0,
    dataServiceCost: 0,
    total: 0,
    includesEstimates: false,
    unpricedTokens: 0,
  };
  for (const record of records) {
    if (record.cost_usd === null || record.cost_usd === undefined) {
      if (record.source !== 'tool_api')
        summary.unpricedTokens +=
          record.prompt_tokens + record.completion_tokens;
      continue;
    }
    if (record.source === 'tool_api')
      summary.dataServiceCost += record.cost_usd;
    else summary.modelCost += record.cost_usd;
    if (record.cost_estimated) summary.includesEstimates = true;
  }
  summary.total = summary.modelCost + summary.dataServiceCost;
  return summary;
}

/**
 * USD with cents, or sub-cent precision for tiny non-zero amounts so a few
 * calls don't read as $0.00.
 */
export function formatUsd(amount: number): string {
  if (amount > 0 && amount < 0.01) return `$${amount.toFixed(4)}`;
  return `$${amount.toFixed(2)}`;
}
